import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  Badge,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Slider,
  TextField as MuiTextField
} from "@material-ui/core";
import {
  AccountCircle,
  Comment as CommentIcon,
  SystemUpdateAlt as SystemUpdateAltIcon
} from "@material-ui/icons";
import { useForm, Controller } from "react-hook-form";
import _ from "lodash";
import styled from "styled-components";
import { DebounceInput } from "react-debounce-input";
import BlockButton from "../../waybee-ui/BlockButton/BlockButton";
import TableGrid from "../../waybee-ui/TableGrid";
import DialogContent from "../../waybee-ui/Dialog/DialogContent";
import { Can } from "../../acl/can";

const TextField = styled(MuiTextField)`
  .MuiOutlinedInput-root {
    height: 50px;
    margin-bottom: 0;
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: ${props =>
      props.error === false ? props.theme.color.primary.main : "none"};
  }
`;

const GradesList = ({
  onSubmit,
  students,
  maxValue,
  average,
  defaultValues,
  concept
}) => {
  const { t } = useTranslation();
  const { handleSubmit, control, watch, setValue } = useForm({
    defaultValues
  });
  const [openComment, setOpenComment] = useState({});
  const [filter, setFilter] = useState({});
  const [filteredStudets, setFilteredStudets] = useState(students);
  const [bulkGrade, setBulkGrade] = useState("");

  const applyBulkGrade = value => {
    const defaults = (defaultValues && defaultValues.grades) || {};
    students.forEach(student => {
      let nextValue = value;
      if (value === "" || value == null) {
        const saved = defaults[student.ra];
        nextValue =
          saved === undefined || saved === null || saved === "" ? null : saved;
      }
      setValue(`grades.${student.ra}`, nextValue, {
        shouldDirty: true,
        shouldValidate: true
      });
    });
  };

  return (
    <>
      <DialogContent>
        <Box mt={1} mb={2}>
          <form noValidate autoComplete="off">
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={6}>
                <DebounceInput
                  minLength={3}
                  debounceTimeout={400}
                  element={TextField}
                  value={filter.name}
                  onChange={e => {
                    setFilter({ name: e.target.value });
                    setFilteredStudets(
                      _.filter(students, student =>
                        _.includes(
                          student.name.toLowerCase(),
                          e.target.value.toLowerCase()
                        )
                      )
                    );
                  }}
                  label={t("grades.v1.searchStudent")}
                  fullWidth
                  startAdornment={(
                    <InputAdornment position="start">
                      <AccountCircle />
                    </InputAdornment>
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={4} style={{ marginLeft: "auto" }}>
                {!concept ? (
                  <TextField
                    label={t("grades.v1.massGrade")}
                    variant="outlined"
                    fullWidth
                    type="number"
                    inputProps={{ step: 0.1, min: 0, max: maxValue }}
                    value={bulkGrade}
                    helperText={t("grades.v1.massHelp")}
                    onChange={e => {
                      const { value } = e.target;
                      if (value !== "" && Number(value) > maxValue) {
                        return;
                      }
                      setBulkGrade(value);
                      applyBulkGrade(value === "" ? "" : Number(value));
                    }}
                  />
                ) : (
                  <Select
                    fullWidth
                    displayEmpty
                    variant="outlined"
                    value={bulkGrade}
                    onChange={e => {
                      const { value } = e.target;
                      setBulkGrade(value);
                      applyBulkGrade(value);
                    }}
                  >
                    <MenuItem value="">
                      <em>{t("grades.v1.massGrade")}</em>
                    </MenuItem>
                    {concept.map(grade => (
                      <MenuItem
                        key={grade.CODCONCEITO}
                        value={grade.CODCONCEITO}
                      >
                        {grade.CODCONCEITO}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Grid>
            </Grid>
          </form>
        </Box>

        <Box>
          <Box mb={2}>
            <TableGrid>
              {filteredStudets.map(student => (
                <TableGrid.Row
                  key={student.id}
                  id={student.rollCallNum}
                  photo={student.photo}
                  photoName={student.name}
                >
                  <TableGrid.Group xs={8}>
                    <TableGrid.Title xs={12}>{student.name}</TableGrid.Title>
                    <TableGrid.Text xs={12}>
                      {`${t("common.labels.ra")}: ${student.ra}`}
                    </TableGrid.Text>
                  </TableGrid.Group>
                  <TableGrid.Cell xs={3}>
                    {!concept ? (
                      <Controller
                        name={`grades.${student.ra}`}
                        control={control}
                        rules={{ max: maxValue, min: 0 }}
                        render={({ field }) => (
                          <Grid container justify="center">
                            <Grid item xs={9}>
                              <TextField
                                variant="outlined"
                                fullWidth
                                type="number"
                                step={0.1}
                                {...field}
                                value={
                                  field.value === null ||
                                  field.value === undefined
                                    ? ""
                                    : field.value
                                }
                                onChange={e => {
                                  const { value: raw } = e.target;
                                  if (raw === "") {
                                    field.onChange(null);
                                    return;
                                  }
                                  if (Number(raw) > maxValue) return;
                                  field.onChange(e);
                                }}
                                error={
                                  watch("grades")[student.ra] &&
                                  watch("grades")[student.ra] < average
                                }
                              />
                            </Grid>
                            <Slider
                              aria-label={t("grades.v1.grade")}
                              step={0.5}
                              max={maxValue}
                              min={0}
                              valueLabelDisplay="auto"
                              value={field.value ? field.value : 0}
                              onChange={(e, newValue) => {
                                field.onChange(newValue);
                              }}
                            />
                          </Grid>
                        )}
                      />
                    ) : (
                      <Controller
                        name={`grades.${student.ra}`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            fullWidth
                            labelId="concep-grades-label"
                            value={field.value || ""}
                            onChange={e => {
                              field.onChange(e.target.value);
                            }}
                          >
                            {concept.map(grade => (
                              <MenuItem
                                key={grade.CODCONCEITO}
                                value={grade.CODCONCEITO}
                              >
                                {grade.CODCONCEITO}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    )}
                  </TableGrid.Cell>
                  <TableGrid.Cell xs={1}>
                    <IconButton
                      onClick={() =>
                        setOpenComment({
                          ...openComment,
                          [student.ra]: !openComment[student.ra]
                        })}
                    >
                      <Badge
                        color="error"
                        variant="dot"
                        invisible={!student.comment}
                      >
                        <CommentIcon />
                      </Badge>
                    </IconButton>
                  </TableGrid.Cell>

                  {openComment[student.ra] && (
                    <TableGrid.Group xs={12}>
                      <TableGrid.Text xs={12}>
                        {t("grades.v1.comment")}
                      </TableGrid.Text>

                      <TableGrid.Cell xs={12}>
                        <Controller
                          name={`comments.${student.ra}`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              variant="outlined"
                              fullWidth
                              type="text"
                              disabled={_.isNil(watch("grades")[student.ra])}
                              {...field}
                              onChange={e => {
                                if (e.target.value > maxValue) return;
                                field.onChange(e);
                              }}
                            />
                          )}
                        />
                      </TableGrid.Cell>
                    </TableGrid.Group>
                  )}
                </TableGrid.Row>
              ))}
            </TableGrid>
          </Box>
        </Box>
      </DialogContent>
      <Box py={1}>
        <Can I="save" a="scoresV1">
          <BlockButton
            onClick={handleSubmit(onSubmit)}
            startIcon={<SystemUpdateAltIcon />}
          >
            {t("grades.v1.release")}
          </BlockButton>
        </Can>
      </Box>
    </>
  );
};

GradesList.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  students: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  maxValue: PropTypes.number.isRequired,
  average: PropTypes.number.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  defaultValues: PropTypes.object.isRequired,
  concept: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

export default GradesList;
